import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { SettingsCard } from "../SettingsCard";
import { useAudioRecorder } from "../../../hooks/useAudioRecorder";
import { ChatIcon, ChatPanel, CountBadge } from "../../../components/support/ChatPanel";
import { EditTicketModal } from "../../../components/support/EditTicketModal";
import { SupportBanner } from "../../../components/support/SupportBanner";
import { refreshSupportUnread, useSupportUnread } from "../../../hooks/useSupportUnread";
import {
  AttachmentType,
  ConfirmAttachmentItem,
  SupportTicket,
  SupportTicketAPI,
  TicketAttachment,
  TicketMessage,
  uploadBlobToS3,
} from "../../../services/supportTicket.service";

// ── Helpers ───────────────────────────────────────────────────────────────────

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

interface PendingAttachment {
  id:              string;
  attachment_type: AttachmentType;
  file_name:       string;
  mime_type:       string;
  blob:            Blob;
  label:           string;
  previewUrl:      string;
}

const STATUS_STYLES: Record<string, string> = {
  open:        "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  resolved:    "bg-green-50 text-green-700 border-green-200",
  closed:      "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Inline playback/preview for one attachment — audio player, image, or video player. */
const AttachmentPreview: React.FC<{ type: AttachmentType; src: string; fileName: string }> = ({ type, src, fileName }) => {
  if (type === "voice_note") {
    return <audio controls preload="metadata" src={src} className="h-9 w-full max-w-xs" />;
  }
  if (type === "photo") {
    return (
      <a href={src} target="_blank" rel="noreferrer">
        <img src={src} alt={fileName} className="h-28 w-auto max-w-xs rounded-lg border border-gray-200 object-cover" />
      </a>
    );
  }
  return <video controls preload="metadata" src={src} className="max-h-56 w-full max-w-sm rounded-lg border border-gray-200" />;
};

const ATTACHMENT_TYPE_LABELS: Record<AttachmentType, string> = {
  voice_note: "Voice note",
  photo: "Photo",
  screen_recording: "Screen recording",
};

const PastAttachment: React.FC<{ attachment: TicketAttachment }> = ({ attachment: a }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2">
    <p className="text-xs text-gray-600 mb-1.5">
      {ATTACHMENT_TYPE_LABELS[a.attachment_type]} · {a.file_name}
      {a.file_size_bytes != null && ` (${formatBytes(a.file_size_bytes)})`}
    </p>
    {a.view_url ? (
      <AttachmentPreview type={a.attachment_type} src={a.view_url} fileName={a.file_name} />
    ) : (
      <p className="text-xs text-gray-400 italic">Not available in this environment.</p>
    )}
  </div>
);

const PencilIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const SupportTickets: React.FC = () => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);
  const [chatTicketId, setChatTicketId] = useState<string | null>(null);
  const [editingTicket, setEditingTicket] = useState<SupportTicket | null>(null);
  const [searchParams] = useSearchParams();
  const { summary, markAllRead } = useSupportUnread("clinic");

  const { isRecording, duration, startRecording, stopRecording } = useAudioRecorder();
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const screenRecorderRef = useRef<MediaRecorder | null>(null);
  const screenChunksRef = useRef<Blob[]>([]);
  const [isRecordingScreen, setIsRecordingScreen] = useState(false);

  const loadTickets = useCallback(() => {
    setLoadingTickets(true);
    SupportTicketAPI.listMine()
      .then(setTickets)
      .catch(() => { /* non-critical — list just stays empty */ })
      .finally(() => setLoadingTickets(false));
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const markTicketRead = useCallback((ticketId: string) => {
    setTickets(prev => prev.map(t => (t.ticket_id === ticketId ? { ...t, unread_count: 0 } : t)));
    SupportTicketAPI.markRead(ticketId).then(refreshSupportUnread).catch(() => { /* next poll corrects it */ });
  }, []);

  // New support replies show up without a manual refresh. While a chat is open,
  // anything that arrives is read immediately.
  useEffect(() => {
    const id = window.setInterval(() => {
      SupportTicketAPI.listMine()
        .then(fresh => {
          setTickets(fresh);
          const open = fresh.find(t => t.ticket_id === chatTicketId);
          if (open && open.unread_count > 0) markTicketRead(open.ticket_id);
        })
        .catch(() => { /* silent */ });
    }, 20_000);
    return () => window.clearInterval(id);
  }, [chatTicketId, markTicketRead]);

  const openChat = useCallback((ticketId: string) => {
    setExpandedTicketId(ticketId);
    setChatTicketId(ticketId);
    markTicketRead(ticketId);
  }, [markTicketRead]);

  // Deep link from the bell / banner: /settings?section=support&ticket=<id>
  const deepLinkTicket = searchParams.get("ticket");
  useEffect(() => {
    if (deepLinkTicket && tickets.some(t => t.ticket_id === deepLinkTicket)) openChat(deepLinkTicket);
    // Only when the deep-link target or the loaded list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTicket, loadingTickets]);

  // Attachment view_urls are short-lived presigned GETs (5 min) — refetch the
  // single ticket for fresh links right before showing them, rather than
  // relying on the ones from the list fetched possibly minutes ago.
  const toggleTicket = async (ticketId: string) => {
    if (expandedTicketId === ticketId) {
      setExpandedTicketId(null);
      return;
    }
    setExpandedTicketId(ticketId);
    setLoadingDetailId(ticketId);
    try {
      const fresh = await SupportTicketAPI.get(ticketId);
      setTickets(prev => prev.map(t => (t.ticket_id === ticketId ? fresh : t)));
    } catch {
      toast.error("Could not load this ticket's attachments.");
    } finally {
      setLoadingDetailId(null);
    }
  };

  const handleReplied = (ticketId: string, msg: TicketMessage) => {
    setTickets(prev => prev.map(t => {
      if (t.ticket_id !== ticketId) return t;
      // Mirror the server: a clinic reply reopens a resolved/closed ticket.
      const reopened = t.status === "resolved" || t.status === "closed";
      return { ...t, status: reopened ? "open" : t.status, messages: [...t.messages, msg] };
    }));
  };

  const deleteTicket = async (t: SupportTicket) => {
    if (!window.confirm(`Delete "${t.subject}"? This removes the ticket, its attachments and the whole conversation, and cannot be undone.`)) return;
    try {
      await SupportTicketAPI.remove(t.ticket_id);
      setTickets(prev => prev.filter(x => x.ticket_id !== t.ticket_id));
      if (chatTicketId === t.ticket_id) setChatTicketId(null);
      if (expandedTicketId === t.ticket_id) setExpandedTicketId(null);
      refreshSupportUnread();
      toast.success("Ticket deleted.");
    } catch (err: any) {
      toast.error(err?.message || "Could not delete the ticket.");
    }
  };

  const unsendMessage = async (ticketId: string, messageId: string) => {
    try {
      await SupportTicketAPI.unsend(ticketId, messageId);
      setTickets(prev => prev.map(x => (x.ticket_id !== ticketId ? x : {
        ...x,
        messages: x.messages.map(m => (m.message_id === messageId ? { ...m, is_deleted: true, body: "", attachments: [] } : m)),
      })));
    } catch (err: any) {
      toast.error(err?.message || "Could not unsend the message.");
    }
  };

  const addAttachment = (att: Omit<PendingAttachment, "previewUrl">) => {
    if (attachments.length >= MAX_ATTACHMENTS) {
      toast.error(`You can attach up to ${MAX_ATTACHMENTS} files per ticket.`);
      return;
    }
    if (att.blob.size > MAX_FILE_SIZE_BYTES) {
      toast.error("That file is larger than the 50 MB limit.");
      return;
    }
    setAttachments(prev => [...prev, { ...att, previewUrl: URL.createObjectURL(att.blob) }]);
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const target = prev.find(a => a.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  };

  // Revoke every pending object URL on unmount so they don't leak.
  useEffect(() => {
    return () => { attachments.forEach(a => URL.revokeObjectURL(a.previewUrl)); };
    // Only needs to run once, on unmount — intentionally not re-running per attachments change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Voice note ───────────────────────────────────────────────────────────

  const handleVoiceClick = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob) {
        addAttachment({
          id: crypto.randomUUID(),
          attachment_type: "voice_note",
          file_name: `voice-note-${Date.now()}.webm`,
          mime_type: "audio/webm",
          blob,
          label: `Voice note (${duration}s)`,
        });
      }
      return;
    }
    try {
      await startRecording();
    } catch {
      toast.error("Microphone access is required to record a voice note.");
    }
  };

  // ── Photo ────────────────────────────────────────────────────────────────

  const handlePhotoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    addAttachment({
      id: crypto.randomUUID(),
      attachment_type: "photo",
      file_name: file.name,
      mime_type: file.type || "image/jpeg",
      blob: file,
      label: file.name,
    });
  };

  // ── Screen recording ─────────────────────────────────────────────────────

  const handleScreenRecordClick = async () => {
    if (isRecordingScreen) {
      screenRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      screenRecorderRef.current = recorder;
      screenChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) screenChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(screenChunksRef.current, { type: "video/webm" });
        screenChunksRef.current = [];
        stream.getTracks().forEach(t => t.stop());
        setIsRecordingScreen(false);
        addAttachment({
          id: crypto.randomUUID(),
          attachment_type: "screen_recording",
          file_name: `screen-recording-${Date.now()}.webm`,
          mime_type: "video/webm",
          blob,
          label: "Screen recording",
        });
      };
      // Stopping via the browser's own "Stop sharing" control.
      stream.getVideoTracks()[0].addEventListener("ended", () => recorder.stop());

      recorder.start();
      setIsRecordingScreen(true);
    } catch {
      toast.error("Screen recording was not started (permission denied or unsupported browser).");
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────

  const resetForm = () => {
    setSubject("");
    setDescription("");
    setAttachments(prev => {
      prev.forEach(a => URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      toast.error("Please fill in a subject and description.");
      return;
    }

    setSubmitting(true);
    try {
      let confirmedAttachments: ConfirmAttachmentItem[] = [];

      if (attachments.length > 0) {
        const { files } = await SupportTicketAPI.requestUploadUrls(
          attachments.map(a => ({
            attachment_type: a.attachment_type,
            file_name: a.file_name,
            mime_type: a.mime_type,
            file_size_bytes: a.blob.size,
          })),
        );

        await Promise.all(
          files.map((item, idx) => {
            const att = attachments[idx];
            if (!item.upload_url) return Promise.resolve(); // dev mode, S3 disabled
            return uploadBlobToS3(item.upload_url, att.blob, att.mime_type);
          }),
        );

        confirmedAttachments = files.map((item, idx) => ({
          s3_key: item.s3_key,
          attachment_type: item.attachment_type,
          file_name: attachments[idx].file_name,
          mime_type: attachments[idx].mime_type,
          file_size_bytes: attachments[idx].blob.size,
        }));
      }

      await SupportTicketAPI.create({
        subject: subject.trim(),
        description: description.trim(),
        attachments: confirmedAttachments,
      });

      toast.success("Support ticket submitted. Our team will get back to you shortly.");
      resetForm();
      loadTickets();
    } catch (err: any) {
      toast.error(err?.message || "Could not submit the ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const chatTicket = tickets.find(t => t.ticket_id === chatTicketId) ?? null;
  const activeTickets = tickets.filter(t => t.status === "open" || t.status === "in_progress");
  const resolvedTickets = tickets.filter(t => t.status === "resolved" || t.status === "closed");

  const renderTicket = (t: SupportTicket) => {
    const isExpanded = expandedTicketId === t.ticket_id;
    const chatOpen = chatTicketId === t.ticket_id;
    return (
      <li key={t.ticket_id} className="overflow-hidden rounded-xl border-2 border-gray-300 bg-white shadow-sm">
        <div className="flex items-stretch bg-gray-50">
          <button
            type="button"
            onClick={() => toggleTicket(t.ticket_id)}
            className="min-w-0 flex-1 px-4 py-3 text-left hover:bg-gray-100"
          >
            <p className="truncate text-sm font-semibold text-gray-900">{t.subject}</p>
            <p className="mt-1 text-[11px] text-gray-400">
              {new Date(t.created_at).toLocaleString()}
              {t.attachments.length > 0 && ` · ${t.attachments.length} attachment${t.attachments.length > 1 ? "s" : ""}`}
            </p>
          </button>
          <div className="flex flex-shrink-0 items-center gap-3 pr-4">
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[t.status] ?? ""}`}>
              {STATUS_LABELS[t.status] ?? t.status}
            </span>
            <button
              type="button"
              onClick={() => (chatOpen ? setChatTicketId(null) : openChat(t.ticket_id))}
              title="Chat with the support team"
              aria-label="Chat with the support team"
              className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                chatOpen ? "border-sky-700 bg-sky-700 text-white" : "border-sky-300 bg-sky-100 text-sky-700 hover:bg-sky-200"
              }`}
            >
              <ChatIcon className="h-4 w-4" />
              <CountBadge count={t.unread_count} className="absolute -right-1.5 -top-1.5" />
            </button>
            {(t.status === "open" || t.status === "in_progress") && (
              <button
                type="button"
                onClick={() => setEditingTicket(t)}
                title="Edit ticket (details and attachments)"
                aria-label="Edit ticket"
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
              >
                <PencilIcon />
              </button>
            )}
            <button
              type="button"
              onClick={() => deleteTicket(t)}
              title="Delete ticket"
              aria-label="Delete ticket"
              className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-red-200 bg-white text-red-600 hover:bg-red-50"
            >
              <TrashIcon />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="grid grid-cols-1 gap-2 border-t-2 border-gray-300 p-3 text-[11px] leading-snug sm:grid-cols-2">
            <div className="min-w-0 rounded-lg border-2 border-gray-200 bg-gray-50 p-2.5">
              <p className="mb-1 font-semibold uppercase tracking-wide text-gray-500">Ticket details</p>
              <p className="max-h-28 overflow-y-auto whitespace-pre-wrap break-words text-gray-800">{t.description}</p>
            </div>
            <div className="min-w-0 rounded-lg border-2 border-gray-200 bg-gray-50 p-2.5">
              <p className="mb-1 font-semibold uppercase tracking-wide text-gray-500">Attachments</p>
              {loadingDetailId === t.ticket_id ? (
                <p className="text-gray-500">Loading…</p>
              ) : t.attachments.length === 0 ? (
                <p className="text-gray-400">None</p>
              ) : (
                <div className="max-h-28 space-y-2 overflow-y-auto">
                  {t.attachments.map(a => <PastAttachment key={a.attachment_id} attachment={a} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 rounded-xl border-2 border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sky-600 text-white">
          <ChatIcon />
        </span>
        <p>
          <span className="font-semibold">Need to talk to us?</span> Click the{" "}
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-sky-300 bg-sky-100 align-middle text-sky-700">
            <ChatIcon className="h-3 w-3" />
          </span>{" "}
          chat icon on any ticket below to chat with the support team, see your earlier messages and share more screenshots or recordings.
        </p>
      </div>

      <SettingsCard
        bordered
        title="Raise a support ticket"
        subtitle="Describe the issue you're facing. You can attach a voice note, a photo, or a screen recording to help us understand it faster. After you submit, use the 💬 chat icon on your ticket to talk to the support team."
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={200}
              placeholder="A short summary of the issue"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={5000}
              rows={5}
              placeholder="Tell us what happened, what you expected, and any steps to reproduce"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Attachments (optional)</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleVoiceClick}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  isRecording ? "border-red-300 bg-red-50 text-red-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {isRecording ? `● Recording ${duration}s — click to stop` : "🎤 Record voice note"}
              </button>

              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                📷 Attach photo
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelected}
                className="hidden"
              />

              <button
                type="button"
                onClick={handleScreenRecordClick}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  isRecordingScreen ? "border-red-300 bg-red-50 text-red-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {isRecordingScreen ? "● Recording screen — click to stop" : "🖥 Record screen"}
              </button>
            </div>

            {attachments.length > 0 && (
              <ul className="mt-3 space-y-2">
                {attachments.map(a => (
                  <li key={a.id} className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="truncate">{a.label} <span className="text-gray-400">({formatBytes(a.blob.size)})</span></span>
                      <button type="button" onClick={() => removeAttachment(a.id)} className="ml-2 text-gray-400 hover:text-red-600 flex-shrink-0">
                        Remove
                      </button>
                    </div>
                    <AttachmentPreview type={a.attachment_type} src={a.previewUrl} fileName={a.file_name} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit ticket"}
            </button>
          </div>
        </form>
      </SettingsCard>

      {chatTicket && (
        <ChatPanel
          floating
          title="Support team"
          subtitle={chatTicket.subject}
          ownRole="clinic"
          otherLabel="Support team"
          messages={chatTicket.messages}
          placeholder="Write a message or report another issue…"
          requestUploadUrls={SupportTicketAPI.requestUploadUrls}
          onSend={(body, atts) => SupportTicketAPI.reply(chatTicket.ticket_id, body, atts)}
          onSent={msg => handleReplied(chatTicket.ticket_id, msg)}
          onUnsend={id => unsendMessage(chatTicket.ticket_id, id)}
          onClose={() => setChatTicketId(null)}
        />
      )}

      {editingTicket && (
        <EditTicketModal
          ticket={editingTicket}
          onClose={() => setEditingTicket(null)}
          onSaved={updated => setTickets(prev => prev.map(x => (x.ticket_id === updated.ticket_id ? updated : x)))}
        />
      )}

      <SupportBanner
        summary={summary}
        from="the support team"
        onOpen={openChat}
        onDismiss={markAllRead}
      />

      <SettingsCard bordered title="Open tickets" subtitle="Requests the support team is still working on. Click the 💬 chat icon on any ticket to chat with the support team.">
        {loadingTickets ? (
          <p className="text-xs text-gray-500">Loading…</p>
        ) : activeTickets.length === 0 ? (
          <p className="text-xs text-gray-500">No open tickets.</p>
        ) : (
          <ul className="space-y-4">{activeTickets.map(renderTicket)}</ul>
        )}
      </SettingsCard>

      <SettingsCard bordered title="Resolved tickets" subtitle="Closed out by the support team — kept for reference. You can still click the 💬 chat icon to message the team (this reopens the ticket).">
        {loadingTickets ? (
          <p className="text-xs text-gray-500">Loading…</p>
        ) : resolvedTickets.length === 0 ? (
          <p className="text-xs text-gray-500">Nothing resolved yet.</p>
        ) : (
          <ul className="space-y-4">{resolvedTickets.map(renderTicket)}</ul>
        )}
      </SettingsCard>
    </div>
  );
};
